import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import useAdminUser from "../../../data/useAdminUser";
import {
  DEFAULT_UTM_MARKERS,
  V1_UTM_MARKERS,
  V2_UTM_MARKERS,
  inspectExternalUtm,
  inspectInternalUtm,
} from "../../../utils/utmMarkers";
import {
  AboutMemo,
  AdminMemo,
  DirectMemo,
  GlossaryMemo,
  LaunchMemo,
  ModerationMemo,
  RetargetingMemo,
} from "./guidePagesA";
import {
  DspMemo,
  ExtraTrafficMemo,
  FundingMemo,
  OrdMemo,
  PopMemo,
  RetargeoMemo,
  TrackersMemo,
} from "./guidePagesB";
import "./Memos.scss";

const MEMOS = [
  {
    slug: "markup",
    title: "Разметка",
    summary: "Три версии: как собрать шаблон, проверить и отдать площадке",
    tag: "UTM",
    keywords: "utm метки макрос term campaign content medium source prg_code other площадка ссылка баннер prg site_id версия 3 v3",
    Page: null,
  },
  {
    slug: "about",
    title: "Что такое программатик",
    summary: "Канал, алгоритм, роль трафик-менеджера и отделы",
    tag: "Онбординг",
    keywords: "программатик ssp ретаргетинг трафик менеджер отделы аккаунтинг производство kpi",
    Page: AboutMemo,
  },
  {
    slug: "glossary",
    title: "Словарь и каналы",
    summary: "Внутренние слова и таблица PRG",
    tag: "Справочник",
    keywords: "prg кт ос рр оп bid rich involtra sape getintent hybrid mybid ssp",
    Page: GlossaryMemo,
  },
  {
    slug: "admin",
    title: "Админка и отчёт производства",
    summary: "Шапка проекта, свод, лимиты, графики и строчка ОП",
    tag: "Админка",
    keywords: "отчет производства лимиты свод шапка графики клиенты цикл rr",
    Page: AdminMemo,
  },
  {
    slug: "launch",
    title: "Запуск клиента в ЛК",
    summary: "Чат, регистрация кабинета, охватная РК, сегмент и баланс",
    tag: "ЛК",
    keywords: "чат регистрация кабинет кампания сегмент баланс 120 метрика объявление",
    Page: LaunchMemo,
  },
  {
    slug: "direct",
    title: "Кабинет Директа",
    summary: "Регистрация клиента и данные рекламодателя",
    tag: "Директ",
    keywords: "директ регистрация инн договор агентство programmatic-ru",
    Page: DirectMemo,
  },
  {
    slug: "moderation",
    title: "Модерация и ЕРИД",
    summary: "Как проверить токен ОРД и одобрить объявление",
    tag: "ЛК",
    keywords: "модерация ерид орд инн реквизиты одобрить баннер",
    Page: ModerationMemo,
  },
  {
    slug: "retargeting",
    title: "Ретаргетинг",
    summary: "Цели, look-alike, динамические триггеры",
    tag: "ЛК",
    keywords: "ретаргетинг цели lookalike динамический метрика частота prgr",
    Page: RetargetingMemo,
  },
  {
    slug: "extra-traffic",
    title: "Доп. трафик из ЛК",
    summary: "Итоговая ссылка, коды PRG и раздельные CPC/CPM",
    tag: "Трафик",
    keywords: "итоговая ссылка площадка prg site_id cpc cpm объявление",
    Page: ExtraTrafficMemo,
  },
  {
    slug: "trackers",
    title: "Сторонние трекеры",
    summary: "Пиксели показа и клика, ЯММ и смешивание каналов",
    tag: "Трафик",
    keywords: "smartis ямм пиксель show click postview dmp sape bid",
    Page: TrackersMemo,
  },
  {
    slug: "retargeo",
    title: "Retargeo",
    summary: "Модерация селф-сервиса, ссылки PRG4/5/7 и Директ",
    tag: "Retargeo",
    keywords: "retargeo модерация дисклеймер инн prg4 prg5 prg7 директ",
    Page: RetargeoMemo,
  },
  {
    slug: "ord",
    title: "ОРД и Медиаскаут",
    summary: "Креативы, цепочки РД–РА–РР и самореклама",
    tag: "ОРД",
    keywords: "орд медиаскаут ерид договор креатив рекламодатель агентство бс пг",
    Page: OrdMemo,
  },
  {
    slug: "pop",
    title: "Bid, MyBid, Rich, Инволтра",
    summary: "Создание РК и блеклист на pop-площадках",
    tag: "Площадки",
    keywords: "bid mybid richads involtra popunder blacklist prg84 prg80 prg81 prg8",
    Page: PopMemo,
  },
  {
    slug: "dsp",
    title: "Sape, Getintent, Hybrid",
    summary: "Отчёты, рекламодатель, креативы и базовые настройки",
    tag: "Площадки",
    keywords: "sape getintent hybrid dsp орд отчёт креатив cpm",
    Page: DspMemo,
  },
  {
    slug: "funding",
    title: "Пополнение площадок",
    summary: "Как пополнить Bid, Rich и Инволтру без хранения паролей",
    tag: "Операции",
    keywords: "пополнение usdt баланс инволтра дима оплата",
    Page: FundingMemo,
  },
];

const FULL_MEMOS_EMAILS = ["e.usikova@programmatic.ru", "a.tuhtaev@programmatic.ru"];

function canSeeAllMemos(email) {
  return FULL_MEMOS_EMAILS.includes(String(email || "").toLowerCase());
}

const SHUFFLED_UTM =
  "utm_source=Programmatic&utm_medium=cpc&utm_campaign={PRG}&utm_content={CAMPAIGN_ID}&utm_term={BANNER_ID}";
const EXTERNAL_EXAMPLE =
  "https://example.com/?utm_source=Programmatic&prg_code=8f14e45f-ea9c-4b1c-9b3a-0e1f2a3b4c5d/2c7a1b90-4e33-4a12-9f08-6d5c4b3a2e10/PRG5/site12&other={CLICK_ID}";
const V3_GLUED =
  "utm_source=Programmatic&utm_medium={PRG}{SITE_ID}&prg_code={CAMPAIGN_ID}/{BANNER_ID}/{PRG}/{SITE_ID}";
const V2_SHUFFLED =
  "utm_source=Programmatic&utm_term={BANNER_ID}/{CAMPAIGN_ID}/{SITE_ID}";

const PRG_CODE_PARTS = [
  { token: "{CAMPAIGN_ID}", label: "Кампания", hint: "не убирать", required: true },
  { token: "{BANNER_ID}", label: "Баннер", hint: "не убирать", required: true },
  { token: "{PRG}", label: "Партнёр", hint: "не убирать", required: true },
  { token: "{SITE_ID}", label: "Площадка SSP", hint: "не убирать", required: true },
];

const V2_TERM_PARTS = [
  { token: "{CAMPAIGN_ID}", label: "Кампания", hint: "обязательно", required: true },
  { token: "{BANNER_ID}", label: "Баннер", hint: "обязательно", required: true },
  { token: "{PRG}", label: "Партнёр", hint: "можно убрать", required: false },
  { token: "{SITE_ID}", label: "Площадка SSP", hint: "можно убрать", required: false },
];

const V1_FIELDS = [
  { name: "utm_medium", token: "{PRG}", label: "Партнёр" },
  { name: "utm_campaign", token: "{CAMPAIGN_ID}", label: "Кампания" },
  { name: "utm_content", token: "{BANNER_ID}", label: "Баннер" },
  { name: "utm_term", token: "{SITE_ID}", label: "Площадка" },
];

function CodeBlock({ value, label }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="Memos-code">
      {label ? <div className="Memos-codeLabel">{label}</div> : null}
      <div className="Memos-codeRow">
        <code translate="no">{value}</code>
        <button
          type="button"
          onClick={() => {
            navigator.clipboard.writeText(value).then(() => {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1400);
            });
          }}
        >
          {copied ? "Скопировано" : "Копировать"}
        </button>
      </div>
    </div>
  );
}

function CheckResult({ result, emptyHint }) {
  const empty = result.issues.length === 1 && result.issues[0].tone === "muted";
  const state = empty ? "empty" : result.ok ? "ok" : "bad";
  return (
    <div className={`Memos-result is-${state}`} role="status" aria-live="polite">
      <div className="Memos-resultTitle">
        {empty ? emptyHint : result.ok ? "Можно использовать" : "Нужно исправить"}
      </div>
      <ul>
        {result.issues.map((issue) => (
          <li key={issue.text} className={`is-${issue.tone}`}>
            {issue.text}
          </li>
        ))}
      </ul>
    </div>
  );
}

function MarkupChecker() {
  const [mode, setMode] = useState("internal");
  const [value, setValue] = useState("");
  const result = useMemo(
    () => (mode === "internal" ? inspectInternalUtm(value) : inspectExternalUtm(value)),
    [mode, value]
  );

  const sample = mode === "internal" ? DEFAULT_UTM_MARKERS : EXTERNAL_EXAMPLE;
  const placeholder =
    mode === "internal"
      ? "utm_source=Programmatic&prg_code={CAMPAIGN_ID}/{BANNER_ID}/{PRG}/{SITE_ID}"
      : "https://site.ru/?utm_source=Programmatic&prg_code=…/…/PRG5/site&other={CLICK_ID}";

  return (
    <section className="Memos-checker" id="check">
      <div className="Memos-checkerHead">
        <h2>Проверить ссылку</h2>
        <p>Вставьте строку - проверка сразу. Ничего сохранять не нужно.</p>
      </div>
      <div className="Memos-modes">
        <button
          type="button"
          aria-pressed={mode === "internal"}
          className={mode === "internal" ? "is-active" : undefined}
          onClick={() => setMode("internal")}
        >
          Шаблон для кабинета
        </button>
        <button
          type="button"
          aria-pressed={mode === "external"}
          className={mode === "external" ? "is-active" : undefined}
          onClick={() => setMode("external")}
        >
          Ссылка для площадки
        </button>
      </div>

      <p className="Memos-modeHint">
        {mode === "internal"
          ? "Строка из формы объявления, ещё со скобками. Нужны {CAMPAIGN_ID} и {BANNER_ID}."
          : "Готовая ссылка для площадки: вместо скобок уже UUID, макрос площадки только в other."}
      </p>

      <label htmlFor="memo-utm-check">Ссылка или UTM</label>
      <textarea
        id="memo-utm-check"
        spellCheck={false}
        autoComplete="off"
        name="utm-check"
        placeholder={placeholder}
        value={value}
        onChange={(event) => setValue(event.target.value)}
      />
      <div className="Memos-checkerActions">
        <button type="button" className="Memos-textBtn" onClick={() => setValue(sample)}>
          Вставить пример
        </button>
        {value ? (
          <button type="button" className="Memos-textBtn" onClick={() => setValue("")}>
            Очистить
          </button>
        ) : null}
      </div>
      <CheckResult
        result={result}
        emptyHint={mode === "internal" ? "Ждёт шаблон с нашими макросами" : "Ждёт ссылку без наших макросов"}
      />
    </section>
  );
}

function Rows({ items }) {
  return (
    <ol className="Memos-rows" translate="no">
      {items.map((item) => (
        <li key={item.text || item.token}>
          <code>{item.text || item.token}</code>
          <span>{item.label}{item.hint ? ` · ${item.hint}` : ""}</span>
        </li>
      ))}
    </ol>
  );
}

function Path({ parts }) {
  return (
    <div className="Memos-pathLine" translate="no">
      {parts.map((part, index) => (
        <span key={part.token} className="Memos-pathPiece">
          {index > 0 ? <span className="Memos-pathSlash" aria-hidden="true">/</span> : null}
          <span className={part.required === false ? "Memos-chip" : "Memos-chip is-on"}>
            <code>{part.token}</code>
            <b>{part.label}</b>
          </span>
        </span>
      ))}
    </div>
  );
}

function BeforeAfter({ from, to, fromLabel = "В шаблоне", toLabel = "После клика" }) {
  return (
    <div className="Memos-swap">
      <div>
        <div className="Memos-swapLabel">{fromLabel}</div>
        <code translate="no">{from}</code>
      </div>
      <div className="Memos-swapArrow" aria-hidden="true">↓</div>
      <div>
        <div className="Memos-swapLabel">{toLabel}</div>
        <code translate="no">{to}</code>
      </div>
    </div>
  );
}

function RulePair({ can, cannot }) {
  return (
    <div className="Memos-rules">
      <div className="is-ok">
        <h3>Можно</h3>
        <ul>
          {can.map((item) => (
            <li key={typeof item === "string" ? item : item.key}>{typeof item === "string" ? item : item.text}</li>
          ))}
        </ul>
      </div>
      <div className="is-bad">
        <h3>Нельзя</h3>
        <ul>
          {cannot.map((item) => (
            <li key={typeof item === "string" ? item : item.key}>{typeof item === "string" ? item : item.text}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ExampleCard({ ok, title, code, note }) {
  return (
    <article className={ok ? "is-ok" : "is-bad"}>
      <h3>{title}</h3>
      <div>
        <code translate="no">{code}</code>
        {note ? <p>{note}</p> : null}
      </div>
    </article>
  );
}

function MarkupMemo() {
  return (
    <>
      <nav className="Memos-jump" aria-label="Разделы">
        <a href="#start">Как работать</a>
        <a href="#link">Ссылка</a>
        <a href="#macros">Макросы</a>
        <a href="#versions">Версии</a>
        <a href="#launch">Запуск</a>
        <a href="#check">Проверка</a>
      </nav>
      <div className="Memos-page">
        <div className="Memos-article">
          <section className="Memos-section" id="start">
            <h2>Как работать с разметкой</h2>
            <p>
              Разметка - это хвост ссылки после знака вопроса. По нему кабинет и Метрика понимают,
              из какой кампании пришёл визит, какой баннер показали и с какой площадки.
            </p>
            <p>
              В объявлении хранится шаблон, не готовая ссылка. При клике кабинет сам подставляет id.
              Собирайте разметку только формой-конструктором в объявлении. Итоговую ссылку руками не правьте.
            </p>
            <div className="Memos-callout">
              <p>
                После любых изменений обязательно нажмите «Проверить разметку».
                Если проверка не проходит - не сохраняйте.
              </p>
            </div>
          </section>
          <section className="Memos-section" id="link">
            <h2>Ссылка</h2>
            <p>
              Человек кликает по баннеру и открывает сайт. После адреса идёт хвост - с него Метрика понимает кампанию.
            </p>
            <Rows
              items={[
                { text: "https://shop.ru/sale", label: "посадочная, сайт" },
                { text: "? utm_source=Programmatic", label: "параметр: имя = значение" },
                { text: "& prg_code=…/PRG5/site12", label: "ещё параметр, через &" },
              ]}
            />
            <p>
              В объявлении хранится шаблон со скобками. При клике кабинет подставляет id сам.
              Посадочную пишите без utm - кабинет допишет хвост.
            </p>
          </section>

          <section className="Memos-section" id="macros">
            <h2>Макросы</h2>
            <p>
              Макрос - заготовка в скобках. UUID руками не пишите: кабинет подставит значение при клике.
            </p>
            <BeforeAfter from="{CAMPAIGN_ID}" to="8f14e45f-ea9c-4b1c-9b3a-0e1f2a3b4c5d" />
            <div className="Memos-macroGrid">
              <div>
                <code translate="no">{"{CAMPAIGN_ID}"}</code>
                <span>кампания</span>
              </div>
              <div>
                <code translate="no">{"{BANNER_ID}"}</code>
                <span>баннер</span>
              </div>
              <div>
                <code translate="no">{"{PRG}"}</code>
                <span>партнёр, PRG5</span>
              </div>
              <div>
                <code translate="no">{"{SITE_ID}"}</code>
                <span>площадка SSP</span>
              </div>
            </div>
            <p>
              Чипы в форме сами ставят слэш между макросами и не дают вставить один макрос дважды.
              Чужие макросы площадки вроде <code translate="no">{"{CLICK_ID}"}</code> и{" "}
              <code translate="no">%%SITE%%</code> - только в поле <code translate="no">other</code>.
            </p>
          </section>

          <section className="Memos-section" id="versions">
            <h2>Версии разметки</h2>
            <p>
              В конструкторе три схемы. Они отличаются тем, в каком параметре лежат наши id.
              Версию выбирают чипами в форме - поля между версиями не копируются.
            </p>
            <p>
              Для новых объявлений берите <strong>версию 3</strong>. Старые кампании на 1 и 2 не переписывайте:
              статистика по ним считается как раньше.
            </p>
            <div className="Memos-panel is-now" id="v3">
              <h3>Версия 3 <span className="Memos-schemeBadge">новые объявления</span></h3>
              <p>
                Id лежат в <code translate="no">prg_code</code>, через слэш, в этом порядке. UTM можно не заполнять.
              </p>
              <Path parts={PRG_CODE_PARTS} />
              <CodeBlock label="Шаблон" value={DEFAULT_UTM_MARKERS} />
              <p>Для Retargeo тот же шаблон, только <code translate="no">utm_source=Retargeo</code>.</p>
              <RulePair
                can={[
                  "Оставить UTM пустыми",
                  "Писать в UTM свой текст, например sale",
                  "Макрос площадки - в other",
                ]}
                cannot={[
                  "Менять prg_code и порядок внутри него",
                  "Склеивать макросы без слэша: {PRG}{SITE_ID}",
                  "Класть макрос площадки в UTM",
                  "Править готовую ссылку руками",
                ]}
              />
              <div className="Memos-examples">
                <ExampleCard ok title="Можно" code={DEFAULT_UTM_MARKERS} />
                <ExampleCard ok={false} title="Нельзя - макросы вплотную" code={V3_GLUED} />
              </div>
            </div>

            <div className="Memos-panel" id="v2">
              <h3>Версия 2 <span className="Memos-schemeBadge">старые кампании</span></h3>
              <p>
                Id идут в <code translate="no">utm_term</code> через слэш: сначала кампания, потом баннер.
                Уже запущенные не переписывайте.
              </p>
              <Path parts={V2_TERM_PARTS} />
              <CodeBlock label="Шаблон" value={V2_UTM_MARKERS} />
              <RulePair
                can={[
                  "Оставить в term только {CAMPAIGN_ID}/{BANNER_ID}",
                  "Убрать PRG или SITE_ID",
                ]}
                cannot={[
                  "Ставить баннер раньше кампании",
                  "Класть макрос площадки в term",
                  "Править готовую ссылку руками",
                ]}
              />
              <div className="Memos-examples">
                <ExampleCard ok title="Можно" code={V2_UTM_MARKERS} />
                <ExampleCard ok={false} title="Нельзя - перепутан порядок" code={V2_SHUFFLED} />
              </div>
            </div>

            <div className="Memos-panel" id="v1">
              <h3>Версия 1 <span className="Memos-schemeBadge">старые кампании</span></h3>
              <p>Каждый id в своём поле. Местами не менять.</p>
              <Rows
                items={V1_FIELDS.map((field) => ({
                  text: `${field.name}=${field.token}`,
                  label: field.label,
                }))}
              />
              <CodeBlock label="Шаблон" value={V1_UTM_MARKERS} />
              <RulePair
                can={[
                  "В medium оставить {PRG} или написать текст",
                  "Убрать SITE_ID, если площадка его не даёт",
                ]}
                cannot={[
                  "Класть кампанию в content, баннер в campaign",
                  "Класть макрос площадки в UTM",
                  "Править готовую ссылку руками",
                ]}
              />
              <div className="Memos-examples">
                <ExampleCard ok title="Можно" code={V1_UTM_MARKERS} />
                <ExampleCard ok={false} title="Нельзя - макросы перепутаны" code={SHUFFLED_UTM} />
              </div>
            </div>
          </section>

          <section className="Memos-section" id="launch">
            <h2>Запуск</h2>
            <ol className="Memos-steps">
              <li>Откройте объявление и соберите разметку формой-конструктором. Ссылку вручную не редактируйте.</li>
              <li>Для новых объявлений выберите версию 3.</li>
              <li>Посадочную пишите без utm и без prg_code.</li>
              <li>После любых правок нажмите «Проверить разметку». Без зелёной проверки не сохраняйте.</li>
              <li>Сохраняется только по «Сохранить» или «Далее».</li>
              <li>Площадке отдайте ссылку из формы, уже без наших скобок. Вместо {"{PRG}"} напишите PRG5.</li>
              <li>Макрос площадки - только в поле other конструктора.</li>
            </ol>
            <CodeBlock label="Ссылка для площадки" value={EXTERNAL_EXAMPLE} />
          </section>
        </div>
        <MarkupChecker />
      </div>
    </>
  );
}

function memoHaystack(memo) {
  return [memo.title, memo.summary, memo.tag, memo.keywords].filter(Boolean).join(" ").toLowerCase();
}

function memoCountLabel(count) {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return `${count} памятка`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} памятки`;
  return `${count} памяток`;
}

function MemosIndex({ memos }) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return memos;
    return memos.filter((memo) => memoHaystack(memo).includes(q));
  }, [query, memos]);

  return (
    <div className="Memos-catalog">
      <div className="Memos-toolbar">
        <label htmlFor="memo-search" className="Memos-srOnly">Поиск по памяткам</label>
        <input
          id="memo-search"
          className="Memos-searchInput"
          type="search"
          name="memo-search"
          autoComplete="off"
          spellCheck={false}
          placeholder="Найти памятку"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <span className="Memos-toolbarCount">
          {filtered.length ? memoCountLabel(filtered.length) : "Ничего не найдено"}
        </span>
      </div>
      {filtered.length ? (
        <div className="Memos-cards">
          {filtered.map((memo) => (
            <Link key={memo.slug} className="Memos-card" to={`/admin/memos/${memo.slug}`}>
              {memo.tag ? <span className="Memos-cardTag">{memo.tag}</span> : null}
              <h2 className="Memos-cardTitle">{memo.title}</h2>
              <p className="Memos-cardSummary">{memo.summary}</p>
              <span className="Memos-cardOpen">Открыть</span>
            </Link>
          ))}
        </div>
      ) : (
        <div className="Memos-empty">
          <p>По запросу «{query.trim()}» ничего нет.</p>
          <button type="button" className="Memos-textBtn" onClick={() => setQuery("")}>
            Сбросить поиск
          </button>
        </div>
      )}
    </div>
  );
}

function MemoBody({ slug }) {
  if (slug === "markup") return <MarkupMemo />;
  const current = MEMOS.find((item) => item.slug === slug);
  if (current?.Page) {
    const Page = current.Page;
    return <Page />;
  }
  return null;
}

export default function Memos() {
  const { slug } = useParams();
  const { user, isLoading } = useAdminUser();
  const seeAll = canSeeAllMemos(user?.email);
  const allowedMemos = seeAll ? MEMOS : MEMOS.filter((item) => item.slug === "markup");
  const current = allowedMemos.find((item) => item.slug === slug);
  const rootRef = useRef(null);
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const scroller = root.closest(".AdminMainLayout");
    const readTop = () => (scroller ? scroller.scrollTop : window.scrollY);
    const onScroll = () => {
      const top = readTop();
      setCompact((was) => (was ? top > 8 : top > 24));
    };
    onScroll();
    const target = scroller || window;
    target.addEventListener("scroll", onScroll, { passive: true });
    return () => target.removeEventListener("scroll", onScroll);
  }, [slug]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const sticky = root.querySelector(".Memos-sticky");
    const jump = root.querySelector(".Memos-jump");
    const apply = () => {
      root.style.setProperty("--memos-sticky-h", `${sticky?.offsetHeight || 0}px`);
      root.style.setProperty("--memos-nav-h", `${jump?.offsetHeight || 0}px`);
    };
    apply();
    const observer = new ResizeObserver(apply);
    if (sticky) observer.observe(sticky);
    if (jump) observer.observe(jump);
    return () => observer.disconnect();
  }, [slug, compact]);

  if (isLoading) return null;

  if (!seeAll && slug && slug !== "markup") {
    return <Navigate to="/admin/memos/markup" replace />;
  }

  if (!seeAll && !slug) {
    return <Navigate to="/admin/memos/markup" replace />;
  }

  if (slug && !current) {
    return <Navigate to="/admin/memos" replace />;
  }

  return (
    <div className={compact ? "Memos is-compact" : "Memos"} ref={rootRef}>
      <div className="Memos-sticky">
        <div className="Memos-header">
          {current && seeAll ? (
            <Link className="Memos-back" to="/admin/memos">
              <span className="Memos-backArrow" aria-hidden="true">←</span>
              Все памятки
            </Link>
          ) : null}
          <div className="Memos-headerText">
            <h1>{current ? current.title : "Памятка"}</h1>
            <p>
              {current
                ? current.summary
                : "Обучающие материалы для запуска кампаний"}
            </p>
          </div>
        </div>
      </div>
      {current ? <MemoBody slug={current.slug} /> : <MemosIndex memos={allowedMemos} />}
    </div>
  );
}
